import type { Matter, MatterContactConnection } from "@/lib/types";
import { isVehicleContactConnection } from "@/lib/utils/connections/vehicle-contact-connections";
import { getInsuranceContactConnections } from "@/lib/utils/connections/insurance-contact-connections";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";

/** Role passed into per-party insurance and vehicle sections. */
export type MatterPartySlideoverRole = "Client" | "Defendant" | "OtherParty";

/**
 * Role for per-party vehicle sections (matter information). Includes other matter
 * contacts that are not client/defendant; those flows do not enforce insured/driver
 * slideover rules the same way as client or defendant.
 */
export type MatterPartyVehicleSlideoverRole = MatterPartySlideoverRole;

/** Narrow defendant picker in slideover when opened from a specific defendant card. */
export function defendantContactIdContextForParty(
  partyRole: MatterPartyVehicleSlideoverRole,
  party: MatterContactConnection,
): { defendantContactIdContext?: string } {
  if (partyRole !== "Defendant") {
    return {};
  }
  return {
    defendantContactIdContext: party.contactId,
  };
}

/** Insurances linked to a party via Insured / Policy Holder connections for that contact. */
export const getPartyInsurances = (
  matter: Matter,
  party: MatterContactConnection,
) => {
  const partyContactId = party.contactId;
  return matter.insurances.filter((insurance) =>
    [
      ...getInsuranceContactConnections(insurance, "Insured"),
      ...getInsuranceContactConnections(insurance, "Policy Holder"),
    ].some((connection) => connection.targetObjectId === partyContactId),
  );
};

/** Insurances that belong to the matter but do not yet have an insured or policy holder in this matter. */
export const getUnassignedMatterInsurances = (matter: Matter) => {
  const matterContactIds = new Set(
    getMatterContactConnections(matter).map(
      (connection) => connection.contactId,
    ),
  );
  return matter.insurances.filter(
    (insurance) =>
      ![
        ...getInsuranceContactConnections(insurance, "Insured"),
        ...getInsuranceContactConnections(insurance, "Policy Holder"),
      ].some((connection) => matterContactIds.has(connection.targetObjectId)),
  );
};

/** Vehicles linked to a party via any vehicle connection for that contact. */
export const getPartyVehicles = (
  matter: Matter,
  party: MatterContactConnection,
) => {
  const partyContactId = party.contactId;
  return matter.vehicles.filter((vehicle) =>
    vehicle.connections.some(
      (connection) =>
        isVehicleContactConnection(connection) &&
        connection.targetObjectId === partyContactId,
    ),
  );
};

/** Vehicles that belong to the matter but do not yet have a contact association in this matter. */
export const getUnassignedMatterVehicles = (matter: Matter) => {
  const matterContactIds = new Set(
    getMatterContactConnections(matter).map(
      (connection) => connection.contactId,
    ),
  );
  return matter.vehicles.filter(
    (vehicle) =>
      !vehicle.connections.some(
        (connection) =>
          isVehicleContactConnection(connection) &&
          matterContactIds.has(connection.targetObjectId),
      ),
  );
};
